import React from 'react'
import { Grid as BaseGrid, Hidden as BaseHidden, styled } from '@mui/material'
import {
  Close as BaseCloseIcon,
  Info as BaseInfoIcon,
  Search as BaseSearchIcon,
  Delete as BaseDeleteIcon
} from '@mui/icons-material'
import { Spinner, InputPrefix, CheckBox as BaseCheckBox } from '@chilecompra/react-kit/components'

export const DesktopNavigation = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 58%;

  ${props => props.theme.breakpoints.down('lg')} {
    gap: 0;
    width: 100%;
  }
`

export const CheckBox = styled(BaseCheckBox)`
  .MuiCheckbox-root {
    margin: 0;
    padding: 0;
  }
  &.MuiFormControlLabel-root {
    margin: 0 8px 0 0;
  }
`

export const Title = styled('h1')`
  color: ${props => props.theme.palette.brand.black1};
  font-family: ${props => props.theme.typography.fontFamily};
  font-feature-settings:
    'clig' off,
    'liga' off;
  font-size: 27px;
  font-style: normal;
  font-weight: 700;
  line-height: 31px;

  ${props => props.theme.breakpoints.down('sm')} {
    font-size: 22px;
    line-height: 26px;
    margin: 0;
    text-align: left;
  }
`

export const Strong = styled('span')`
  font-weight: bold;
`

export const PhoneNumberPrefix = styled(InputPrefix)`
  padding: 0.375rem 0.75rem;
`

export const Image = styled('img')`
  margin-top: -80px;
  width: 110%;
`

export const Instructions = styled('p')`
  color: ${props => props.theme.palette.brand.black1};
  font-family: ${props => props.theme.typography.fontFamily};
  font-feature-settings:
    'clig' off,
    'liga' off;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  margin: 0;

  ${props => props.theme.breakpoints.down('sm')} {
    text-align: left;
  }
`

export const Form = styled('form')`
  width: 80%;
  ${props => props.theme.breakpoints.down('lg')} {
    margin-top: -15px;
    width: 100%;
  }
  label {
    color: ${props => props.theme.palette.brand.black1};
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    text-align: left;
  }
`

export const Search = styled(({ clear, loading, ...props }) => <button {...props} />)`
  align-items: center;
  background-color: ${props => (props.clear ? props.theme.palette.brand.black3 : props.theme.palette.brand.primary)};
  border: 0;
  border-radius: 0;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  height: 100%;
  justify-content: center;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  outline: 0;
  pointer-events: ${props => (props.disabled ? 'none' : 'initial')};
  user-select: ${props => (props.disabled ? 'none' : 'initial')};
  width: 40px;

  &:focus {
    outline: 0;
  }

  &:hover {
    background-color: ${props => (props.clear ? props.theme.palette.gray[35] : props.theme.palette.blue[35])};
  }
`

export const SearchClearIcon = styled(BaseCloseIcon)`
  color: ${props => props.theme.palette.brand.white};
  height: 24px;
  width: 24px;
`

export const SearchIcon = styled(BaseSearchIcon)`
  color: ${props => props.theme.palette.brand.white};
  height: 24px;
  width: 24px;
`

export const SearchLoadingIcon = styled(props => <Spinner {...props} size="18px" />)`
  color: ${props => props.theme.palette.brand.white};
  & .MuiCircularProgress-root {
    color: ${props => props.theme.palette.brand.white};
  }
`
export const MobileNavigation = styled('div')`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 18px;
  justify-content: center;
  margin: 30px auto;
  width: 90%;
`

export const Root = styled('div')`
  box-sizing: border-box;
  padding: 0 0 130px;
  width: 100%;
`

export const Section = styled(({ margin, maxWidth, ...props }) => <BaseGrid {...props} />)`
  margin: ${props => props.margin || 'initial'};
  max-width: ${props => props.maxWidth || 'initial'};

  label {
    color: ${props => props.theme.palette.brand.black1};
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    text-align: left;
  }
`

export const Hidden = styled(BaseHidden)``

export const ToastContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;

  & span {
    color: ${props => props.theme.palette.brand.black1};
    font-feature-settings:
      'liga' off,
      'clig' off;
    font-family: ${props => props.theme.typography.fontFamily};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }
`

export const StrongToast = styled('strong')`
  color: ${props => props.theme.palette.brand.black1};
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px;
`

export const Divider = styled('div')`
  margin-bottom: 15px;
`

export const Link = styled('a')`
  color: ${props => props.theme.palette.brand[props.color]};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  text-decoration: underline;

  &:hover {
    color: ${({ color, theme }) => {
      switch (color) {
        case 'primary':
          return theme.palette.blue[35]
        case 'black2':
          return theme.palette.brand.black3
        default:
          return 'inherit'
      }
    }};
  }
`

export const DeleteTypography = styled('p')`
  color: ${props => props.theme.palette.brand.black3};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  margin: 0;
  text-align: center;
  cursor: pointer;
`

export const DeleteIcon = styled(BaseDeleteIcon)`
  color: ${props => props.theme.palette.custom.iconGray};
  height: 18px;
  width: 18px;
  cursor: pointer;
`

export const AttachmentSection = styled('section')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 20px;

  ${props => props.theme.breakpoints.down('sm')} {
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0.2);
    flex-direction: column;
    height: 220px;
    justify-content: center;
    margin-bottom: 30px;
    padding: 30px;
  }

  && button {
    background-color: ${props => props.disabled && props.theme.palette.custom.dividerGray};
    border-color: ${props => props.disabled && props.theme.palette.custom.dividerGray};
    color: ${props => props.disabled && props.theme.palette.brand.white};
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    margin-top: -25px;
    margin-right: -25px;
    padding: 18px;
    width: 180px;

    ${props => props.theme.breakpoints.down('sm')} {
      margin-right: 0;
      margin-top: -20px;
    }

    &:hover {
      background-color: ${props => props.disabled && props.theme.palette.custom.dividerGray};
      border-color: ${props => props.disabled && props.theme.palette.custom.dividerGray};
      color: ${props => props.disabled && props.theme.palette.brand.white};
    }
  }
`

export const AttachmentSubTitle = styled('h2')`
  color: ${props => props.theme.palette.brand.black3};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  margin-top: 22px;
  text-align: left;
`
export const AttachmentButtonSection = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 170px;
  justify-content: center;
  z-index: 0;
  && p {
    margin: -90px -60px 0 0;
    z-index: 1;

    ${props => props.theme.breakpoints.down('sm')} {
      margin: -80px 0 0 0;
    }
  }
`
